/* eslint-disable @typescript-eslint/naming-convention */
import {Agent} from 'https';

import {fetch, formData} from '@shopify/cli-kit/node/http';
import {createFileReadStream} from '@shopify/cli-kit/node/fs';
import {
  Logger,
  outputCompleted,
  outputInfo,
} from '@shopify/cli-kit/node/output';
import {joinPath} from '@shopify/cli-kit/node/path';
import {mapLimit} from 'async';

import {deployDefaults} from '../utils/utils.js';
import {MetricsExporter, MetricName} from '../utils/metrics-exporter.js';

import {DeploymentConfig, DeploymentHooks} from './types.js';
import {DeploymentTargetResponse} from './graphql/deployment-initiate.js';

interface InitiateResumableResponse {
  target: DeploymentTargetResponse;
  sessionUri: string;
  location: string;
}

interface ResumableUploadStatus {
  complete: boolean;
  lastReceivedByte: number;
}

interface UploadFilesOptions {
  config: DeploymentConfig;
  hooks?: DeploymentHooks;
  logger: Logger;
  metricsExporter?: MetricsExporter;
  targets: DeploymentTargetResponse[];
}

export async function uploadFiles(options: UploadFilesOptions): Promise<void> {
  const {config, logger, targets, hooks} = options;
  outputInfo(`Uploading ${targets.length} files...`, logger);

  const totalUploadSize =
    targets.reduce((total, target) => total + target.fileSize, 0) / 1024;
  options.metricsExporter?.add(MetricName.TotalSizeUploaded, totalUploadSize);
  options.metricsExporter?.add(MetricName.NumFilesUploaded, targets.length);

  const agent = new Agent({keepAlive: true});
  hooks?.onUploadFilesStart?.();
  const startTime = performance.now();
  return mapLimit(targets, 6, async (target: DeploymentTargetResponse) => {
    await uploadFile(config, target, agent);
  })
    .then(() => {
      const endTime = performance.now();
      hooks?.onUploadFilesComplete?.();
      options.metricsExporter?.add(MetricName.UploadTime, endTime - startTime);
      outputCompleted(`Files uploaded successfully`, logger);
    })
    .catch((err) => {
      hooks?.onUploadFilesError?.(err);
      throw err;
    })
    .finally(() => {
      agent.destroy();
    });
}

async function uploadFile(
  config: DeploymentConfig,
  target: DeploymentTargetResponse,
  agent: Agent,
) {
  const localFolderPath =
    target.fileType === 'WORKER'
      ? joinPath(config.rootPath!, config.workerDir!)
      : joinPath(config.rootPath!, config.assetsDir!);

  if (target.parameters !== null && target.parameters.length > 0) {
    // If parameters exist perform a form upload
    const form = formData();
    target.parameters.forEach((param) => {
      form.append(param.name, param.value);
    });
    form.append(
      'file',
      createFileReadStream(joinPath(localFolderPath, target.filePath)),
    );
    await formUpload(form, target, agent);
  } else {
    // If no parameters exist perform a resumable upload
    const initData = await initiateResumableUpload(target, agent);
    await performResumableUpload(
      joinPath(localFolderPath, target.filePath),
      initData,
      agent,
    );
  }
}

async function formUpload(
  form: ReturnType<typeof formData>,
  target: DeploymentTargetResponse,
  agent: Agent,
  attemptNumber = 0,
) {
  try {
    const timeoutDuration = 120000;
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
    }, timeoutDuration);

    const response = await fetch(target.uploadUrl, {
      method: 'POST',
      body: form,
      signal: controller.signal,
      headers: {
        Connection: 'keep-alive',
      },
      agent,
    });
    clearTimeout(timeout);

    if (!response.ok) {
      throw new Error(`${response.status}`);
    }
  } catch (err) {
    if (isErrorCode(err, 'ENOENT')) {
      throw new Error(`File not found: ${target.filePath}`);
    }

    if (attemptNumber < Number(deployDefaults.maxUploadAttempts)) {
      await formUpload(form, target, agent, attemptNumber + 1);
    } else {
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error(`Request timeout whilst uploading ${target.filePath}`);
      }
      throw new Error(`Failed to upload file ${target.filePath}`);
    }
  }
}

async function initiateResumableUpload(
  target: DeploymentTargetResponse,
  agent: Agent,
): Promise<InitiateResumableResponse> {
  return fetch(target.uploadUrl, {
    method: 'POST',
    headers: {
      'x-goog-resumable': 'start',
      'X-Goog-Content-Length-Range': `0,${target.fileSize}`,
      'User-Agent': 'oxygen-cli',
    },
    agent,
  })
    .then((res) => {
      return {
        sessionUri: res.headers.get('x-guploader-uploadid')!,
        location: res.headers.get('location')!,
        target,
      };
    })
    .catch((err) => {
      throw new Error(
        `Failed to initiate resumable upload for file ${target.filePath} (status code ${err.statusCode})`,
      );
    });
}

async function performResumableUpload(
  localFilePath: string,
  initData: InitiateResumableResponse,
  agent: Agent,
  startByte = 0,
  attemptNumber = 0,
) {
  await uploadResumable(
    initData.location,
    localFilePath,
    startByte,
    agent,
  ).catch(async (err) => {
    if (isErrorCode(err, 'ENOENT')) {
      throw new Error(`File not found: ${initData.target.filePath}`);
    }

    if (
      err &&
      attemptNumber >= Number(deployDefaults.maxResumabeUploadAttempts)
    ) {
      throw new Error(
        `Failed to upload file ${initData.target.filePath} after ${deployDefaults.maxResumabeUploadAttempts} attempts`,
      );
    }
    const status = await resumableUploadStatus(
      initData.location,
      initData.target.fileSize,
      agent,
    );
    if (!status.complete) {
      const nextAttemptStartByte = status.lastReceivedByte;
      const attempt = attemptNumber + 1;
      await performResumableUpload(
        localFilePath,
        initData,
        agent,
        nextAttemptStartByte,
        attempt,
      );
    }
  });
}

async function uploadResumable(
  location: string,
  filePath: string,
  lastReceivedByte: number,
  agent: Agent,
): Promise<number> {
  const file = createFileReadStream(filePath, {start: lastReceivedByte});
  return fetch(location, {
    method: 'PUT',
    body: file,
    agent,
  }).then((res) => {
    return res.status;
  });
}

async function resumableUploadStatus(
  location: string,
  fileSize: number,
  agent: Agent,
): Promise<ResumableUploadStatus> {
  const getLastByte = (range: string | null): number => {
    if (!range || range.split('-').length !== 2) return 0;
    const rangeParts = range.split('-');
    return parseInt(rangeParts[1]!, 10);
  };

  return fetch(location, {
    method: 'PUT',
    headers: {
      'Content-Length': '0',
      'Content-Range': `bytes */${fileSize}`,
    },
    agent,
  })
    .then((res) => {
      return {
        complete: res.status === 200,
        lastReceivedByte: getLastByte(res.headers.get('range')),
      };
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
      return {
        complete: false,
        lastReceivedByte: 0,
      };
    });
}

function isErrorCode(err: unknown, code: string): boolean {
  return err instanceof Error && 'code' in err && err.code === code;
}
